"use client";

import { useEffect } from "react";
import { isBenignLockAbortError } from "@/lib/benign-client-errors";
import { reportClientErrorToSupport } from "@/lib/client-error-report";

/**
 * Registers window-level error listeners so uncaught script errors and promise rejections
 * trigger the same support email pipeline as React error boundaries.
 */
export function ClientErrorReporter() {
  useEffect(() => {
    const onWindowError = (ev: ErrorEvent) => {
      if (ev.filename?.startsWith("chrome-extension://") || ev.filename?.startsWith("moz-extension://")) {
        return;
      }
      const message = ev.message || "Script error";
      if (message === "Script error." && !ev.error) {
        return;
      }
      const stack = ev.error instanceof Error ? ev.error.stack : undefined;
      void reportClientErrorToSupport({
        kind: "window_error",
        message: `${message}${ev.filename ? ` (${ev.filename}:${ev.lineno}:${ev.colno})` : ""}`,
        stack,
      });
    };

    const onRejection = (ev: PromiseRejectionEvent) => {
      const r = ev.reason;
      if (isBenignLockAbortError(r)) {
        ev.preventDefault();
        return;
      }
      const message = r instanceof Error ? r.message : typeof r === "string" ? r : "Unhandled rejection";
      const stack = r instanceof Error ? r.stack : undefined;
      const reason =
        r instanceof Error
          ? undefined
          : typeof r === "object" && r !== null
            ? JSON.stringify(r).slice(0, 500)
            : String(r);
      void reportClientErrorToSupport({
        kind: "unhandled_rejection",
        message,
        stack,
        reason,
      });
    };

    window.addEventListener("error", onWindowError);
    window.addEventListener("unhandledrejection", onRejection);
    return () => {
      window.removeEventListener("error", onWindowError);
      window.removeEventListener("unhandledrejection", onRejection);
    };
  }, []);

  return null;
}
